import * as countries from 'country.json';
import { Country } from 'models/country';
import { Organization } from 'models/organization';

// NOTE: A copy of this files exists in the frontend and backend.
//       Make sure to keep them in sync.

export const countryFromCode = (countryCode?: string) => {
    const country = Object.values(countries).find((country) => country.alpha2Code === countryCode);
    return country;
};

export const formatOrganizationNumber = (organizationNumber: string, countryCode: string) => {
    const country = Object.values(countries).find((country) => country.alpha2Code === countryCode);
    if (
        country?.organizationNumberPattern &&
        country?.organizationNumberPattern !== country?.organizationNumberFormat
    ) {
        return organizationNumber.replace(
            new RegExp(country.organizationNumberPattern),
            (country as Country).organizationNumberReplacement || '$1-$2',
        );
    }
    return organizationNumber;
};

const digitsOf = (value: string) => value.replace(/\D/g, '').split('').map(Number);

const weightedSum = (digits: number[], weights: number[]) =>
    weights.reduce((sum, weight, index) => sum + weight * digits[index], 0);

/**
 * Check digits of the organisation numbers whose rules are public and stable (DD-3700). A copy
 * of shared-denthub-code's domain/organization/organization-number.ts: keep them in sync.
 * Netherlands (KvK) and Germany (register numbers) are checked by format only.
 */
const CHECK_DIGIT_RULES: Record<string, (digits: number[]) => boolean> = {
    // Organisationsnummer: Luhn.
    SE: (digits) =>
        digits.reduce((sum, digit, index) => {
            const doubled = index % 2 === 0 ? digit * 2 : digit;
            return sum + (doubled > 9 ? doubled - 9 : doubled);
        }, 0) %
            10 ===
        0,
    // Organisasjonsnummer: MOD11 with weights 3, 2, 7, 6, 5, 4, 3, 2.
    NO: (digits) => {
        const remainder = weightedSum(digits, [3, 2, 7, 6, 5, 4, 3, 2]) % 11;
        const check = remainder === 0 ? 0 : 11 - remainder;
        return check !== 10 && check === digits[8];
    },
    // CVR: weights 2, 7, 6, 5, 4, 3, 2, 1, sum divisible by 11.
    DK: (digits) => weightedSum(digits, [2, 7, 6, 5, 4, 3, 2, 1]) % 11 === 0,
    // Y-tunnus: weights 7, 9, 10, 5, 8, 4, 2; remainder 1 is never issued.
    FI: (digits) => {
        const remainder = weightedSum(digits, [7, 9, 10, 5, 8, 4, 2]) % 11;
        const check = remainder === 0 ? 0 : 11 - remainder;
        return remainder !== 1 && check === digits[7];
    },
    // NIP: weights 6, 5, 7, 2, 3, 4, 5, 6, 7, mod 11.
    PL: (digits) => {
        const check = weightedSum(digits, [6, 5, 7, 2, 3, 4, 5, 6, 7]) % 11;
        return check !== 10 && check === digits[9];
    },
};

/**
 * Whether an organisation number is valid for its country: it matches the country's format
 * (as typed or after formatting) and, where the country has one, its check digit is right.
 * Countries without a known format accept anything non-empty.
 */
export const isValidOrganizationNumber = (organizationNumber: string, countryCode: string) => {
    const value = organizationNumber.trim();
    if (!value) {
        return false;
    }
    const country = countryFromCode(countryCode);
    if (!country?.organizationNumberFormat) {
        return true;
    }
    const formatted = formatOrganizationNumber(value, countryCode);
    if (!new RegExp(country.organizationNumberFormat).test(formatted)) {
        return false;
    }
    const checkDigits = CHECK_DIGIT_RULES[countryCode];
    return checkDigits ? checkDigits(digitsOf(value)) : true;
};

export const getEnabledCountries = () => {
    const scanCountries = Object.values(countries).filter((country: Country) => {
        return (
            country.alpha2Code === 'DK' || // Denmark
            country.alpha2Code === 'FI' || // Finland
            country.alpha2Code === 'NO' || // Norway
            country.alpha2Code === 'SE' || // Sweden
            country.alpha2Code === 'IS' || // Iceland
            country.alpha2Code === 'EE' || // Estonia
            country.alpha2Code === 'DE' || // Germany (DD-3700)
            country.alpha2Code === 'NL' || // Netherlands (DD-3700)
            country.alpha2Code === 'PL' // Poland (DD-3700)
        );
    });
    return scanCountries;
};

export const DEFAULT_COUNTRY_CODE = 'SE';

/**
 * Country code an organization should default new records to:
 * its own country when that is an enabled country, otherwise Sweden.
 */
export const getOrganizationDefaultCountryCode = (organization?: Partial<Organization>): string => {
    const organizationCountryCode = organization?.contact?.address?.country?.toUpperCase();
    const isEnabledCountry = getEnabledCountries().some((country) => country.alpha2Code === organizationCountryCode);
    return isEnabledCountry && organizationCountryCode ? organizationCountryCode : DEFAULT_COUNTRY_CODE;
};

export const getEUCountries = () => {
    const euCountries = Object.values(countries).filter((country: Country) => {
        return (
            country.alpha2Code === 'AT' || // Austria
            country.alpha2Code === 'BE' || // Belgium
            country.alpha2Code === 'BG' || // Bulgaria
            country.alpha2Code === 'HR' || // Croatia
            country.alpha2Code === 'CY' || // Cyprus
            country.alpha2Code === 'CZ' || // Czech Republic
            country.alpha2Code === 'DK' || // Denmark
            country.alpha2Code === 'EE' || // Estonia
            country.alpha2Code === 'FI' || // Finland
            country.alpha2Code === 'FR' || // France
            country.alpha2Code === 'DE' || // Germany
            country.alpha2Code === 'GR' || // Greece
            country.alpha2Code === 'HU' || // Hungary
            country.alpha2Code === 'IE' || // Ireland
            country.alpha2Code === 'IT' || // Italy
            country.alpha2Code === 'LV' || // Latvia
            country.alpha2Code === 'LT' || // Lithuania
            country.alpha2Code === 'LU' || // Luxembourg
            country.alpha2Code === 'MT' || // Malta
            country.alpha2Code === 'NL' || // Netherlands
            country.alpha2Code === 'PL' || // Poland
            country.alpha2Code === 'PT' || // Portugal
            country.alpha2Code === 'RO' || // Romania
            country.alpha2Code === 'SK' || // Slovakia
            country.alpha2Code === 'SI' || // Slovenia
            country.alpha2Code === 'ES' || // Spain
            country.alpha2Code === 'SE' // Sweden
        );
    });
    return euCountries;
};

export const getEuropeanCountries = () => {
    const europeanCountries = Object.values(countries).filter((country: Country) => {
        return (
            country.alpha2Code === 'AL' || // Albania
            country.alpha2Code === 'AD' || // Andorra
            country.alpha2Code === 'AM' || // Armenia
            country.alpha2Code === 'AT' || // Austria
            country.alpha2Code === 'AZ' || // Azerbaijan
            country.alpha2Code === 'BY' || // Belarus
            country.alpha2Code === 'BE' || // Belgium
            country.alpha2Code === 'BA' || // Bosnia and Herzegovina
            country.alpha2Code === 'BG' || // Bulgaria
            country.alpha2Code === 'HR' || // Croatia
            country.alpha2Code === 'CY' || // Cyprus
            country.alpha2Code === 'CZ' || // Czech Republic
            country.alpha2Code === 'DK' || // Denmark
            country.alpha2Code === 'EE' || // Estonia
            country.alpha2Code === 'FI' || // Finland
            country.alpha2Code === 'FR' || // France
            country.alpha2Code === 'GE' || // Georgia
            country.alpha2Code === 'DE' || // Germany
            country.alpha2Code === 'GR' || // Greece
            country.alpha2Code === 'HU' || // Hungary
            country.alpha2Code === 'IS' || // Iceland
            country.alpha2Code === 'IE' || // Ireland
            country.alpha2Code === 'IT' || // Italy
            country.alpha2Code === 'KZ' || // Kazakhstan
            country.alpha2Code === 'XK' || // Kosovo
            country.alpha2Code === 'LV' || // Latvia
            country.alpha2Code === 'LI' || // Liechtenstein
            country.alpha2Code === 'LT' || // Lithuania
            country.alpha2Code === 'LU' || // Luxembourg
            country.alpha2Code === 'MT' || // Malta
            country.alpha2Code === 'MD' || // Moldova
            country.alpha2Code === 'MC' || // Monaco
            country.alpha2Code === 'ME' || // Montenegro
            country.alpha2Code === 'NL' || // Netherlands
            country.alpha2Code === 'MK' || // North Macedonia
            country.alpha2Code === 'NO' || // Norway
            country.alpha2Code === 'PL' || // Poland
            country.alpha2Code === 'PT' || // Portugal
            country.alpha2Code === 'RO' || // Romania
            country.alpha2Code === 'RU' || // Russia
            country.alpha2Code === 'SM' || // San Marino
            country.alpha2Code === 'RS' || // Serbia
            country.alpha2Code === 'SK' || // Slovakia
            country.alpha2Code === 'SI' || // Slovenia
            country.alpha2Code === 'ES' || // Spain
            country.alpha2Code === 'SE' || // Sweden
            country.alpha2Code === 'CH' || // Switzerland
            country.alpha2Code === 'TR' || // Turkey
            country.alpha2Code === 'UA' || // Ukraine
            country.alpha2Code === 'GB' || // United Kingdom
            country.alpha2Code === 'VA' // Vatican City
        );
    });
    return europeanCountries;
};
